import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiClient, useLanguage, enqueueOfflineAction, updateOfflineQueue } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PageHeader from "../components/layout/PageHeader";
import ArabicText from "../components/shared/ArabicText";
import { getSurah, SURAHS, getSurahTranslations, cacheSurah } from "../services/quranService";
import { getWordByWord } from "../services/tafsirService";
import { toast } from "sonner";
import { updateHafalanProgress, updateReadingProgress, showChallengeProgressToast } from "../utils/challengeHelper";
import AyahRecorder from "../components/quran/AyahRecorder";
import TafsirPanel from "../components/TafsirPanel";
import { useQuranAudio } from "../contexts/QuranAudioContext";
import ReadingMode from "../components/quran/ReadingMode";
import { getQcfSurah } from "../services/qcfService";
import QcfFontFace from "../components/quran/QcfFontFace";
import {
  Bookmark,
  BookmarkPlus,
  Check,
  Play,
  Download,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Loader2,
  X,
  Sparkles
} from "lucide-react";

const PerKataAyahRow = ({ ayah, isEnglish, wwEntry, wwIsLoading, onVisible, stripBismillah }) => {
  const cardRef = useRef(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTriggered.current) {
            hasTriggered.current = true;
            onVisible(ayah.numberInSurah);
          }
        });
      },
      { rootMargin: "200px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ayah.numberInSurah, onVisible]);

  return (
    <Card ref={cardRef} data-testid={`perkata-ayah-${ayah.numberInSurah}`}>
      <CardContent className="p-3 md:p-4">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary/10 text-primary text-xs md:text-sm font-semibold flex items-center justify-center">
            {ayah.numberInSurah}
          </div>
        </div>

        {wwIsLoading ? (
          <div className="flex gap-2 flex-wrap justify-end" dir="rtl">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-16 rounded-lg" />
            ))}
          </div>
        ) : wwEntry?.available && wwEntry.words.length > 0 ? (
          <div className="space-y-4">
<p className="text-right text-2xl md:text-3xl font-uthmani leading-[2.6] pb-6 mb-3 border-b" dir="rtl">{wwEntry.words.map((w) => w.text_arabic).join(" ")}</p>
<div className="flex flex-wrap gap-x-3 gap-y-7 justify-start" dir="rtl">
            {wwEntry.words.map((word) => (
              <div key={word.word_id} className="flex flex-col items-center min-w-[52px] px-2">
                <span className="text-[10px] md:text-xs italic text-muted-foreground mb-1 text-center whitespace-nowrap" dir="ltr">{typeof word.transliteration === "object" ? (word.transliteration?.text || "") : (word.transliteration || "")}</span>
<span className="text-2xl md:text-3xl font-uthmani leading-[2.2] py-1">
                  {word.text_arabic}
                </span>
                <span className="text-[11px] md:text-xs text-muted-foreground mt-3 text-center whitespace-nowrap">
                  {isEnglish ? (word.translation_english || "-") : (word.translation_indonesian || "-")}
                </span>
              </div>
            ))}
          </div>
</div>
        ) : (
          <p className="text-right text-2xl md:text-3xl leading-relaxed" dir="rtl">
            {stripBismillah(ayah.arabic, ayah.numberInSurah)}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const QuranReader = () => {
  const { surahNumber } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language, translation, setTranslation, arabicFont, setArabicFont, quranFontSize } = useLanguage();
  const isEnglish = language === "en";

  // Quran Audio Context
  const {
    currentAyah: audioCurrentAyah,
    isPlaying: audioIsPlaying,
    probingReciter,
    audioDownloadProgress,
    downloadSurahAudio,
    playAyah,
    setCurrentSurah,
  } = useQuranAudio();

  const [surah, setSurah] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookmarks, setBookmarks] = useState([]);
  const [currentAyah, setCurrentAyah] = useState(1); // Local current ayah for display
  const [activeTab, setActiveTab] = useState("ayat");
  const [memorization, setMemorization] = useState(null);
  const [memorizedAyahs, setMemorizedAyahs] = useState([]);
  const [confirmAyah, setConfirmAyah] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [updatingMemorization, setUpdatingMemorization] = useState(false);
  const [surahTranslations, setSurahTranslations] = useState({});
  const [downloadingSurah, setDownloadingSurah] = useState(false);
  const [downloadingAudio, setDownloadingAudio] = useState(false);
  const [qcfData, setQcfData] = useState(null); // { [ayahNumber]: { text, page_number } }
  const [qcfFontsReady, setQcfFontsReady] = useState(false);
  const arabicFontClass = arabicFont === "indopak" ? "font-indopak" : arabicFont === "tajwid" ? "font-tajwid" : "font-uthmani hafssmart";
  const arabicSizeClass = quranFontSize === "small" ? "text-lg md:text-xl" : quranFontSize === "large" ? "text-2xl md:text-3xl" : quranFontSize === "xlarge" ? "text-3xl md:text-4xl" : "text-xl md:text-2xl";
  const [wwCache, setWwCache] = useState({});
  const [wwLoading, setWwLoading] = useState({});
  const tabsContainerRef = useRef(null);
  const ayatRef = useRef(null);
  const membacaRef = useRef(null);
  const perkataRef = useRef(null);
  useEffect(() => {
    const container = tabsContainerRef.current;
    const el =
      activeTab === "ayat"
        ? ayatRef.current
        : activeTab === "membaca"
        ? membacaRef.current
        : perkataRef.current;
    if (!container || !el) return;
    const cWidth = container.clientWidth;
    const elLeft = el.offsetLeft;
    const elWidth = el.offsetWidth;
    const target = elLeft - (cWidth - elWidth) / 2;
    container.scrollTo({ left: Math.max(target, 0), behavior: "smooth" });
  }, [activeTab]);
  const loadWordByWord = async (ayahNumber) => {
    const key = String(ayahNumber);
    if (wwCache[key] || wwLoading[key]) return;
    setWwLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const data = await getWordByWord(parseInt(surahNumber), ayahNumber);
      setWwCache((prev) => ({ ...prev, [key]: data }));
    } catch {
    } finally {
      setWwLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Bookmark/Highlight dialog state
  const [showBookmarkDialog, setShowBookmarkDialog] = useState(false);
  const [selectedAyah, setSelectedAyah] = useState(null);
  const [bookmarkData, setBookmarkData] = useState({
    color: "",
    note: ""
  });

  const OFFLINE_QUEUE_KEY = "quran-offline-queue";
  const BOOKMARKS_CACHE_KEY = "quran-bookmarks-cache";
  const MEMO_CACHE_KEY = "quran-memorization-cache";

  const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;

  const getCachedBookmarks = () => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(localStorage.getItem(BOOKMARKS_CACHE_KEY) || "[]");
    } catch {
      return [];
    }
  };

  const setCachedBookmarks = (next) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(BOOKMARKS_CACHE_KEY, JSON.stringify(next));
  };

  const getCachedMemorization = () => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(localStorage.getItem(MEMO_CACHE_KEY) || "[]");
    } catch {
      return [];
    }
  };

  const setCachedMemorization = (next) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(MEMO_CACHE_KEY, JSON.stringify(next));
  };

  const getQueuedMemorization = () => {
    if (typeof window === "undefined") return null;
    try {
      const queue = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || "[]");
      const target = [...queue].reverse().find((item) =>
        item.type === "memorization-update" && item.payload?.surah_number === parseInt(surahNumber)
      );
      if (!target) return null;
      return {
        mem_id: `offline-${surahNumber}`,
        surah_number: target.payload.surah_number,
        surah_name: target.payload.surah_name,
        total_ayahs: target.payload.total_ayahs,
        memorized_ayahs: target.payload.memorized_ayahs,
      };
    } catch {
      return null;
    }
  };
  const [savingBookmark, setSavingBookmark] = useState(false);

  // Tafsir panel state
  const [showTafsirPanel, setShowTafsirPanel] = useState(false);
  const [tafsirAyah, setTafsirAyah] = useState(null);

  const highlightColors = [
    { value: "yellow", label: isEnglish ? "Yellow" : "Kuning", class: "bg-yellow-200 border-yellow-500" },
    { value: "green", label: isEnglish ? "Green" : "Hijau", class: "bg-green-200 border-green-500" },
    { value: "blue", label: isEnglish ? "Blue" : "Biru", class: "bg-blue-200 border-blue-500" },
    { value: "red", label: isEnglish ? "Red" : "Merah", class: "bg-red-200 border-red-500" },
    { value: "purple", label: isEnglish ? "Purple" : "Ungu", class: "bg-purple-200 border-purple-500" },
  ];
  const arabicFontOptions = [
    { value: "uthmani", label: isEnglish ? "Uthmani" : "Utsmani" },
    { value: "indopak", label: "IndoPak" },
    { value: "tajwid", label: "Tajwid" },
    { value: "qcf", label: isEnglish ? "Madinah Mushaf" : "Mushaf Madinah" },
  ];
  const toArabicDigits = (value) => String(value).replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]);

  useEffect(() => {
    loadSurah();
    loadBookmarks();
    loadMemorization();
    if (arabicFont === "qcf") {
      setQcfFontsReady(false);
      getQcfSurah(parseInt(surahNumber)).then(setQcfData);
    } else {
      setQcfData(null);
      setQcfFontsReady(false);
    }
  }, [surahNumber, arabicFont]);

  useEffect(() => {
    let isMounted = true;
    const loadTranslations = async () => {
      try {
        const translations = await getSurahTranslations(isEnglish ? "en" : "id");
        if (isMounted) {
          setSurahTranslations(translations);
        }
      } catch (error) {
        console.error("Error loading surah translations:", error);
        if (isMounted) {
          setSurahTranslations({});
        }
      }
    };
    loadTranslations();
    return () => {
      isMounted = false;
    };
  }, [isEnglish]);

  useEffect(() => {
    // Update last read when ayah changes
    if (surah && currentAyah) {
      updateLastRead();
    }
  }, [currentAyah, surah]);

  useEffect(() => {
    if (!surah) return;
    const targetAyah = Number(searchParams.get("ayah"));
    if (!Number.isFinite(targetAyah)) return;
    if (targetAyah < 1 || targetAyah > surah.numberOfAyahs) return;
    setActiveTab("ayat");
    setCurrentAyah(targetAyah);
    setTimeout(() => {
      const targetEl = document.getElementById(`ayah-${targetAyah}`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  }, [surah, searchParams]);

  // Handle hash navigation for quick jump to ayah (e.g., #13)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (!hash || !surah) return;

      const ayahNumber = parseInt(hash.replace("#", ""));
      if (isNaN(ayahNumber)) return;
      if (ayahNumber < 1 || ayahNumber > surah.numberOfAyahs) return;

      setActiveTab("ayat");
      setCurrentAyah(ayahNumber);

      setTimeout(() => {
        const targetEl = document.getElementById(`ayah-${ayahNumber}`);
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 500);
    };

    handleHashChange();

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [surah]);

  // Sync surah to audio context when loaded
  useEffect(() => {
    if (surah) {
      setCurrentSurah(surah);
    }
  }, [surah, setCurrentSurah]);

  // Auto-scroll to current ayah during audio playback
  useEffect(() => {
    // Only scroll during playback and if we're on the same surah
    if (!audioIsPlaying || !audioCurrentAyah || !surah) return;

    // Small delay to ensure DOM is updated
    setTimeout(() => {
      const targetEl = document.getElementById(`ayah-${audioCurrentAyah}`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  }, [audioCurrentAyah]);

  const goToSurah = useCallback((direction) => {
    const newNumber = parseInt(surahNumber) + direction;
    if (newNumber >= 1 && newNumber <= 114) {
      navigate(`/quran/${newNumber}`);
    }
  }, [navigate, surahNumber]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!surah || activeTab !== "ayat") return;
      if (showTafsirPanel || showConfirm || showBookmarkDialog) return;

      const target = event.target;
      if (target && typeof target === "object") {
        const tagName = target.tagName?.toUpperCase();
        if (target.isContentEditable || tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
          return;
        }
      }

      if (event.key === "ArrowRight") {
        if (currentAyah < surah.numberOfAyahs) {
          const nextAyah = currentAyah + 1;
          setCurrentAyah(nextAyah);
          setTimeout(() => {
            const targetEl = document.getElementById(`ayah-${nextAyah}`);
            if (targetEl) {
              targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 100);
        }
        event.preventDefault();
        return;
      }

      if (event.key === "ArrowLeft") {
        if (currentAyah > 1) {
          const nextAyah = currentAyah - 1;
          setCurrentAyah(nextAyah);
          setTimeout(() => {
            const targetEl = document.getElementById(`ayah-${nextAyah}`);
            if (targetEl) {
              targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 100);
        }
        event.preventDefault();
        return;
      }

      if (event.key === "PageDown") {
        goToSurah(1);
        event.preventDefault();
        return;
      }

      if (event.key === "PageUp") {
        goToSurah(-1);
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [surah, activeTab, showTafsirPanel, showConfirm, showBookmarkDialog, currentAyah, goToSurah]);

  const loadSurah = async () => {
    setLoading(true);
    try {
      const data = await getSurah(parseInt(surahNumber));
      setSurah(data);
      try {
        const cacheKey = "quran-surah-open-count";
        const raw = localStorage.getItem(cacheKey) || "{}";
        const counts = JSON.parse(raw);
        const key = String(surahNumber);
        const nextCount = (counts[key] || 0) + 1;
        counts[key] = nextCount;
        localStorage.setItem(cacheKey, JSON.stringify(counts));
        if (nextCount >= 3) {
          cacheSurah(parseInt(surahNumber));
        }
      } catch {}

      // Track reading progress for daily challenge
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const storageKey = `quran_read_surahs_${today}`;
      const readSurahs = JSON.parse(localStorage.getItem(storageKey) || '[]');

      // Check if this surah hasn't been read today
      if (!readSurahs.includes(parseInt(surahNumber))) {
        // Update challenge progress
        updateReadingProgress(1).then(challengeResult => {
          if (challengeResult.success) {
            const toastData = showChallengeProgressToast(challengeResult, isEnglish);
            if (toastData) {
              setTimeout(() => {
                toast[toastData.type](toastData.title, { description: toastData.description });
              }, 1000);
            }
          }
        }).catch(err => console.error('Error updating reading progress:', err));

        // Mark this surah as read for today
        readSurahs.push(parseInt(surahNumber));
        localStorage.setItem(storageKey, JSON.stringify(readSurahs));
      }
    } catch (error) {
      console.error("Error loading surah:", error);
      toast.error(isEnglish ? "Failed to load surah" : "Gagal memuat surah");
    } finally {
      setLoading(false);
    }
  };

  const loadBookmarks = async () => {
    try {
      const res = await apiClient.get("/bookmarks");
      setBookmarks(res.data);
      setCachedBookmarks(res.data);
    } catch (error) {
      console.error("Error loading bookmarks:", error);
      if (!isOnline) {
        setBookmarks(getCachedBookmarks());
      }
    }
  };

  const loadMemorization = async () => {
    try {
      const res = await apiClient.get("/memorization");
      const existing = res.data.find((mem) => mem.surah_number === parseInt(surahNumber));
      setMemorization(existing || null);
      setMemorizedAyahs(existing?.memorized_ayahs || []);
      setCachedMemorization(res.data);
    } catch (error) {
      console.error("Error loading memorization:", error);
      const cached = getCachedMemorization();
      const existing = cached.find((mem) => mem.surah_number === parseInt(surahNumber));
      const queued = getQueuedMemorization();
      const merged = queued || existing || null;
      setMemorization(merged);
      setMemorizedAyahs(merged?.memorized_ayahs || []);
    }
  };

  const updateLastRead = async () => {
    if (!isOnline) {
      enqueueOfflineAction({
        id: `offline-last-read-${Date.now()}`,
        type: "last-read",
        payload: {
          surah_number: parseInt(surahNumber),
          ayah_number: currentAyah,
          surah_name: surah?.englishName || `Surah ${surahNumber}`,
        },
        createdAt: Date.now(),
      });
      return;
    }
    try {
      await apiClient.post("/last-read", {
        surah_number: parseInt(surahNumber),
        ayah_number: currentAyah,
        surah_name: surah?.englishName || `Surah ${surahNumber}`,
      });
    } catch (error) {
      console.error("Error updating last read:", error);
    }
  };

  const getAyahBookmark = (ayahNumber) => {
    return bookmarks.find(
      b => b.surah_number === parseInt(surahNumber) && b.ayah_number === ayahNumber
    );
  };

  const toggleBookmark = async (ayahNumber, ayahText = null) => {
    const existing = getAyahBookmark(ayahNumber);

    if (existing) {
      // If bookmark exists, show edit dialog
      setSelectedAyah(ayahNumber);
      setBookmarkData({
        color: existing.color || "",
        note: existing.note || ""
      });
      setShowBookmarkDialog(true);
    } else {
      // If no bookmark, open dialog to create new one
      setSelectedAyah(ayahNumber);
      setBookmarkData({
        color: "",
        note: ""
      });
      setShowBookmarkDialog(true);
    }
  };

  const handleOpenTafsir = (ayahNumber) => {
    setTafsirAyah(ayahNumber);
    setShowTafsirPanel(true);
  };

  const handleSaveBookmark = async () => {
    try {
      setSavingBookmark(true);
      const existing = getAyahBookmark(selectedAyah);
      const surahNameValue = surah?.englishName || `Surah ${surahNumber}`;

      if (existing) {
        if (bookmarkData.color === "" && bookmarkData.note === "") {
          if (!isOnline || existing.bookmark_id?.startsWith("offline-")) {
            const next = bookmarks.filter(b => b.bookmark_id !== existing.bookmark_id);
            setBookmarks(next);
            setCachedBookmarks(next);
            if (existing.bookmark_id?.startsWith("offline-")) {
              updateOfflineQueue((queue) => queue.filter((item) =>
                !(item.type === "bookmark-create" && item.tempId === existing.bookmark_id)
              ));
            } else {
              enqueueOfflineAction({
                id: `offline-bookmark-delete-${Date.now()}`,
                type: "bookmark-delete",
                payload: { bookmark_id: existing.bookmark_id },
                createdAt: Date.now(),
              });
            }
            toast.success(isEnglish ? "Bookmark removed" : "Bookmark dihapus");
          } else {
            await apiClient.delete(`/bookmarks/${existing.bookmark_id}`);
            const next = bookmarks.filter(b => b.bookmark_id !== existing.bookmark_id);
            setBookmarks(next);
            setCachedBookmarks(next);
            toast.success(isEnglish ? "Bookmark removed" : "Bookmark dihapus");
          }
        } else {
          const updateData = {};
          if (bookmarkData.color) updateData.color = bookmarkData.color;
          if (bookmarkData.note) updateData.note = bookmarkData.note;

          if (!isOnline || existing.bookmark_id?.startsWith("offline-")) {
            const next = bookmarks.map(b => b.bookmark_id === existing.bookmark_id ? { ...b, ...updateData } : b);
            setBookmarks(next);
            setCachedBookmarks(next);
            if (existing.bookmark_id?.startsWith("offline-")) {
              updateOfflineQueue((queue) => {
                const nextQueue = [...queue];
                const index = nextQueue.findIndex((item) =>
                  item.type === "bookmark-create" && item.tempId === existing.bookmark_id
                );
                if (index >= 0) {
                  nextQueue[index] = {
                    ...nextQueue[index],
                    payload: { ...nextQueue[index].payload, ...updateData },
                    updatedAt: Date.now(),
                  };
                }
                return nextQueue;
              });
            } else {
              enqueueOfflineAction({
                id: `offline-bookmark-update-${Date.now()}`,
                type: "bookmark-update",
                payload: { bookmark_id: existing.bookmark_id, updateData },
                createdAt: Date.now(),
              });
            }
            toast.success(isEnglish ? "Bookmark updated" : "Bookmark diperbarui");
          } else {
            const res = await apiClient.patch(`/bookmarks/${existing.bookmark_id}`, updateData);
            const next = bookmarks.map(b => b.bookmark_id === existing.bookmark_id ? res.data : b);
            setBookmarks(next);
            setCachedBookmarks(next);
            toast.success(isEnglish ? "Bookmark updated" : "Bookmark diperbarui");
          }
        }
      } else {
        const ayah = surah?.ayahs?.find(a => a.numberInSurah === selectedAyah);
        const payload = {
          surah_number: parseInt(surahNumber),
          ayah_number: selectedAyah,
          surah_name: surahNameValue,
          ayah_text: ayah?.arabic || "",
          color: bookmarkData.color || null,
          note: bookmarkData.note || null
        };
        if (!isOnline) {
          const tempId = `offline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const next = [...bookmarks, { ...payload, bookmark_id: tempId }];
          setBookmarks(next);
          setCachedBookmarks(next);
          enqueueOfflineAction({
            id: `offline-bookmark-create-${Date.now()}`,
            type: "bookmark-create",
            payload,
            tempId,
            createdAt: Date.now(),
          });
          toast.success(isEnglish ? "Bookmark saved offline" : "Bookmark disimpan offline");
        } else {
          const res = await apiClient.post("/bookmarks", payload);
          const next = [...bookmarks, res.data];
          setBookmarks(next);
          setCachedBookmarks(next);
          toast.success(isEnglish ? "Bookmark added" : "Bookmark ditambahkan");
        }
      }

      setShowBookmarkDialog(false);
      setBookmarkData({ color: "", note: "" });
      setSelectedAyah(null);
    } catch (error) {
      console.error("Error saving bookmark:", error);
      const errorMsg = error.response?.data?.detail || error.message || (isEnglish ? "Failed to save bookmark" : "Gagal menyimpan bookmark");
      toast.error(errorMsg);
    } finally {
      setSavingBookmark(false);
    }
  };

  const ensureMemorization = async () => {
    if (memorization) return memorization;
    const surahInfoValue = SURAHS.find((s) => s.number === parseInt(surahNumber));
    if (!surahInfoValue) return null;
    const res = await apiClient.post("/memorization", {
      surah_number: surahInfoValue.number,
      surah_name: surahInfoValue.englishName,
      total_ayahs: surahInfoValue.numberOfAyahs,
    });
    setMemorization(res.data);
    return res.data;
  };

  const updateMemorizedAyahs = async (nextAyahs) => {
    const surahInfoValue = SURAHS.find((s) => s.number === parseInt(surahNumber));
    if (!surahInfoValue) return;
    if (!isOnline) {
      const payload = {
        surah_number: surahInfoValue.number,
        surah_name: surahInfoValue.englishName,
        total_ayahs: surahInfoValue.numberOfAyahs,
        memorized_ayahs: nextAyahs,
      };
      const offlineMem = {
        mem_id: memorization?.mem_id || `offline-${surahNumber}`,
        ...payload,
      };
      setMemorization(offlineMem);
      setMemorizedAyahs(nextAyahs);
      const cached = getCachedMemorization();
      const nextCache = [
        ...cached.filter((mem) => mem.surah_number !== payload.surah_number),
        offlineMem,
      ];
      setCachedMemorization(nextCache);
      updateOfflineQueue((queue) => {
        const nextQueue = queue.filter((item) =>
          !(item.type === "memorization-update" && item.payload?.surah_number === payload.surah_number)
        );
        nextQueue.push({
          id: `offline-memorization-${Date.now()}`,
          type: "memorization-update",
          payload,
          createdAt: Date.now(),
        });
        return nextQueue;
      });
      toast.success(isEnglish ? "Memorization saved offline" : "Hafalan disimpan offline");
      return;
    }
    const mem = await ensureMemorization();
    if (!mem) return;
    setUpdatingMemorization(true);

    // Calculate how many new ayahs were memorized
    const previousAyahs = memorizedAyahs || [];
    const newAyahs = nextAyahs.filter(ayah => !previousAyahs.includes(ayah));
    const ayahsAdded = newAyahs.length;

    try {
      const res = await apiClient.patch(`/memorization/${mem.mem_id}`, {
        memorized_ayahs: nextAyahs
      });
      setMemorization(res.data);
      setMemorizedAyahs(res.data.memorized_ayahs || []);
      const cached = getCachedMemorization();
      const nextCache = [
        ...cached.filter((memItem) => memItem.surah_number !== res.data.surah_number),
        res.data,
      ];
      setCachedMemorization(nextCache);
      toast.success(isEnglish ? "Memorization updated" : "Hafalan diperbarui");

      // Auto-update hafalan challenge progress
      if (ayahsAdded > 0) {
        const challengeResult = await updateHafalanProgress(ayahsAdded);

        // Show toast notification if challenge progress updated
        if (challengeResult.success) {
          const toastData = showChallengeProgressToast(challengeResult, isEnglish);
          if (toastData) {
            setTimeout(() => {
              toast[toastData.type](toastData.title, { description: toastData.description });
            }, 500);
          }
        }
      }
    } catch (error) {
      toast.error(isEnglish ? "Failed to update memorization" : "Gagal memperbarui hafalan");
    } finally {
      setUpdatingMemorization(false);
    }
  };

  const handleDownloadSurah = async () => {
    if (!surah) return;
    setDownloadingSurah(true);
    try {
      await cacheSurah(parseInt(surahNumber));
      toast.success(isEnglish ? "Surah saved for offline" : "Surah disimpan offline");
    } catch (error) {
      toast.error(isEnglish ? "Failed to save surah" : "Gagal menyimpan surah");
    } finally {
      setDownloadingSurah(false);
    }
  };

  const handleDownloadAudio = async () => {
    if (!surah) return;
    setDownloadingAudio(true);
    const result = await downloadSurahAudio(surah);
    if (result.success) {
      toast.success(isEnglish ? "Audio saved for offline" : "Audio disimpan offline");
    } else {
      toast.error(isEnglish ? "Failed to save audio" : "Gagal menyimpan audio");
    }
    setDownloadingAudio(false);
  };

  const handleToggleMemorized = (ayahNumber, checked) => {
    if (checked) {
      setConfirmAyah(ayahNumber);
      setShowConfirm(true);
      return;
    }
    const next = memorizedAyahs.filter((num) => num !== ayahNumber);
    updateMemorizedAyahs(next);
  };

  const handleVoicePassed = (ayahNumber) => {
    const next = Array.from(new Set([...memorizedAyahs, ayahNumber])).sort((a, b) => a - b);
    updateMemorizedAyahs(next);
  };

  const confirmMemorized = () => {
    if (!confirmAyah) return;
    const next = Array.from(new Set([...memorizedAyahs, confirmAyah])).sort((a, b) => a - b);
    setShowConfirm(false);
    setConfirmAyah(null);
    updateMemorizedAyahs(next);
  };

  // Play ayah using global audio context
  const handlePlayAyah = (ayahIndex) => {
    if (!surah) return;
    playAyah(surah, ayahIndex);
    setCurrentAyah(ayahIndex + 1);
  };

  const surahInfo = SURAHS.find(s => s.number === parseInt(surahNumber));
  const surahTranslationLabel = surahInfo ? (surahTranslations[surahInfo.number] || surahInfo.englishNameTranslation) : "";
  const prevSurahNumber = parseInt(surahNumber) - 1;
  const nextSurahNumber = parseInt(surahNumber) + 1;
  const prevSurahInfo = SURAHS.find(s => s.number === prevSurahNumber);
  const nextSurahInfo = SURAHS.find(s => s.number === nextSurahNumber);
  const pageGroups = surah?.ayahs?.reduce((acc, ayah) => {
    const pageNumber = ayah.page || 0;
    if (!acc[pageNumber]) {
      acc[pageNumber] = [];
    }
    acc[pageNumber].push(ayah);
    return acc;
  }, {}) || {};
  const pageNumbers = Object.keys(pageGroups)
    .map((page) => Number(page))
    .filter((page) => Number.isFinite(page) && page > 0)
    .sort((a, b) => a - b);
  const shouldStripBismillah = parseInt(surahNumber) !== 1 && parseInt(surahNumber) !== 9;
  const normalizeAr = (w) =>
    w.replace(/[\u0640\u064B-\u065F\u0670\u06D6-\u06ED\u200B-\u200F]/g, "").replace(/\u0671/g, "\u0627");
  const BISMILLAH_WORDS = ["بسم", "الله", "الرحمن", "الرحيم"];
  const stripBismillah = (text, numberInSurah) => {
    if (!text || numberInSurah !== 1 || !shouldStripBismillah) {
      return text;
    }
    const parts = text.trim().split(/\s+/);
    const head = parts.slice(0, 4).map(normalizeAr);
    if (head.length === 4 && head.every((w, k) => w === BISMILLAH_WORDS[k])) {
      return parts.slice(4).join(" ");
    }
    return text;
  };
  const qcfPending = arabicFont === "qcf" && (!qcfData || !qcfFontsReady);

  return (
    <div className="min-h-screen pb-24 md:pb-32 bg-gradient-to-b from-background to-background/95" data-testid="quran-reader-page">
      {arabicFont === "qcf" && qcfData && (
        <QcfFontFace
          pageNumbers={[...new Set(Object.values(qcfData).map((a) => a.page_number))]}
          onReady={setQcfFontsReady}
        />
      )}
      {/* Premium Header - Like Quran List */}
      <div className="md:sticky md:top-0 z-30 bg-background/80 backdrop-blur-xl border-b">
        <div className="page-container py-3 md:py-4">
          {/* Top Row: Back + Title + Settings */}
          <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-y-2 mb-3 md:mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/quran")}
              className="rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <div className="text-center flex-1 min-w-0 px-2 md:px-4">
              <h1 className="text-xl font-bold">{surah?.englishName || `Surah ${surahNumber}`}</h1>
              <p className="text-sm text-muted-foreground">
                {isEnglish ? (surahInfo?.englishNameTranslation || "") : (surahTranslationLabel || "")}
              </p>
            </div>

            <div className="flex flex-col items-stretch md:items-end gap-2 w-full md:w-auto">
              <div className="flex items-center justify-between md:justify-end gap-2">
                <Select value={translation} onValueChange={setTranslation}>
                  <SelectTrigger className="flex-1 md:flex-none md:w-24 h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="indonesian">Indonesia</SelectItem>
                    <SelectItem value="english">English</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={arabicFont} onValueChange={setArabicFont}>
                  <SelectTrigger className="flex-1 md:flex-none md:w-20 h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {arabicFontOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between md:justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 md:flex-none h-8 text-[10px] md:text-xs"
                  onClick={handleDownloadSurah}
                  disabled={downloadingSurah || !surah}
                >
                  {downloadingSurah ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3 mr-1" />
                  )}
                  {isEnglish ? "Save Surah" : "Simpan Surah"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 md:flex-none h-8 text-[10px] md:text-xs"
                  onClick={handleDownloadAudio}
                  disabled={downloadingAudio || !surah}
                >
                  {downloadingAudio ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3 mr-1" />
                  )}
                  {isEnglish ? "Save Audio" : "Simpan Audio"}
                </Button>
              </div>
              {audioDownloadProgress?.inProgress && (
                <div className="text-[10px] md:text-xs text-muted-foreground">
                  {isEnglish
                    ? `Saving audio ${audioDownloadProgress.downloaded}/${audioDownloadProgress.total}`
                    : `Menyimpan audio ${audioDownloadProgress.downloaded}/${audioDownloadProgress.total}`}
                </div>
              )}
            </div>
          </div>

          {/* Premium Stats Cards - Like Quran List */}
          {surahInfo && (
            <div className="grid grid-cols-4 gap-1.5 md:gap-2">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 border border-emerald-200 dark:border-emerald-900 rounded-lg md:rounded-xl p-1.5 md:p-3 text-center">
                <BookOpen className="w-3.5 h-3.5 md:w-5 md:h-5 mx-auto mb-0.5 md:mb-1 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm md:text-lg font-bold text-emerald-700 dark:text-emerald-300">{surahInfo.number}</p>
                <p className="text-[8px] md:text-[10px] text-muted-foreground">{isEnglish ? "Surah" : "Surah"}</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-900 rounded-lg md:rounded-xl p-1.5 md:p-3 text-center">
                <Bookmark className="w-3.5 h-3.5 md:w-5 md:h-5 mx-auto mb-0.5 md:mb-1 text-blue-600 dark:text-blue-400" />
                <p className="text-sm md:text-lg font-bold text-blue-700 dark:text-blue-300">{surahInfo.numberOfAyahs}</p>
                <p className="text-[8px] md:text-[10px] text-muted-foreground">{isEnglish ? "Verses" : "Ayat"}</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border border-purple-200 dark:border-purple-900 rounded-lg md:rounded-xl p-1.5 md:p-3 text-center">
                <Sparkles className="w-3.5 h-3.5 md:w-5 md:h-5 mx-auto mb-0.5 md:mb-1 text-purple-600 dark:text-purple-400" />
                <p className="text-[10px] md:text-xs font-bold text-purple-700 dark:text-purple-300 leading-tight">
                  {isEnglish
                    ? surahInfo.revelationType
                    : (surahInfo.revelationType === "Meccan" ? "Makkiyah" : "Madaniyah")
                  }
                </p>
                <p className="text-[8px] md:text-[10px] text-muted-foreground">{isEnglish ? "Type" : "Jenis"}</p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200 dark:border-amber-900 rounded-lg md:rounded-xl p-1.5 md:p-3 text-center">
                <Play className="w-3.5 h-3.5 md:w-5 md:h-5 mx-auto mb-0.5 md:mb-1 text-amber-600 dark:text-amber-400" />
                <p className="text-sm md:text-lg font-bold text-amber-700 dark:text-amber-300">{currentAyah}</p>
                <p className="text-[8px] md:text-[10px] text-muted-foreground">{isEnglish ? "Current" : "Saat Ini"}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="page-container space-y-4 pt-4">
        {/* Premium Navigation */}
        <div className="flex items-center justify-between gap-2 md:gap-3">
          <Button
            variant="outline"
            className="flex-1 h-auto md:h-14 py-2 md:py-0 font-medium text-xs md:text-sm"
            onClick={() => goToSurah(-1)}
            disabled={parseInt(surahNumber) <= 1}
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
            <div className="text-left flex-1 min-w-0">
              <div className="text-[10px] md:text-xs opacity-70">{isEnglish ? "Previous" : "Sebelumnya"}</div>
              {prevSurahInfo && (
                <>
                  <div className={`text-[12px] md:text-sm ${arabicFontClass} text-primary/80 truncate`}>
                    {prevSurahInfo.name}
                  </div>
                  <div className="text-[10px] md:text-xs text-muted-foreground truncate">
                    {prevSurahInfo.englishName}
                  </div>
                </>
              )}
            </div>
          </Button>

          <div className="hidden md:block text-center px-3 md:px-6">
            <div className={`text-2xl md:text-3xl font-bold ${arabicFontClass} text-primary mb-0.5 md:mb-1`}>{surahInfo?.name}</div>
            <div className="text-xs md:text-sm text-muted-foreground">{surahInfo?.englishName}</div>
          </div>

          <Button
            variant="outline"
            className="flex-1 h-auto md:h-14 py-2 md:py-0 font-medium text-xs md:text-sm"
            onClick={() => goToSurah(1)}
            disabled={parseInt(surahNumber) >= 114}
          >
            <div className="text-right flex-1 min-w-0">
              <div className="text-[10px] md:text-xs opacity-70">{isEnglish ? "Next" : "Selanjutnya"}</div>
              {nextSurahInfo && (
                <>
                  <div className={`text-[12px] md:text-sm ${arabicFontClass} text-primary/80 truncate`}>
                    {nextSurahInfo.name}
                  </div>
                  <div className="text-[10px] md:text-xs text-muted-foreground truncate">
                    {nextSurahInfo.englishName}
                  </div>
                </>
              )}
            </div>
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5 ml-1 md:ml-2" />
          </Button>
        </div>

        {/* Bismillah */}
        {parseInt(surahNumber) !== 1 && parseInt(surahNumber) !== 9 && (
          <Card className="islamic-gradient text-white">
            <CardContent className="p-4 md:p-6 text-center">
              <p className={`${arabicFontClass} ${arabicSizeClass} leading-relaxed`}>
                بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
              </p>
            </CardContent>
          </Card>
        )}

        {/* Premium Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Premium Tab Header */}
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-6 md:w-8 pointer-events-none bg-gradient-to-r from-background to-background/0"></div>
            <div className="absolute right-0 top-0 bottom-0 w-6 md:w-8 pointer-events-none bg-gradient-to-l from-background to-background/0"></div>
            <div ref={tabsContainerRef} className="flex gap-2 md:gap-3 overflow-x-auto overflow-y-hidden whitespace-nowrap scroll-smooth snap-x snap-mandatory px-1 -mx-1 scrollbar-hide">
            {/* Tab: Ayat */}
            <button
              ref={ayatRef}
              onClick={() => setActiveTab("ayat")}
              className={`relative overflow-hidden rounded-xl md:rounded-2xl p-3 md:p-4 text-left transition-all duration-300 flex-shrink-0 min-w-[180px] snap-center ${
                activeTab === "ayat"
                  ? "bg-gradient-to-br from-primary to-primary/80 text-white shadow-xl shadow-primary/25 scale-[1.02]"
                  : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {activeTab === "ayat" && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
              )}

              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center ${
                    activeTab === "ayat" ? "bg-white/20" : "bg-primary/10"
                  }`}>
                    <Bookmark className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base md:text-lg">
                      {isEnglish ? "Verses" : "Ayat"}
                    </h3>
                    <p className={`text-xs md:text-sm ${activeTab === "ayat" ? "text-white/80" : "text-muted-foreground"}`}>
                      {isEnglish ? "Study mode" : "Mode belajar"}
                    </p>
                  </div>
                </div>

                {activeTab === "ayat" && (
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-white animate-pulse" />
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-white/50 animate-pulse" style={{ animationDelay: "0.2s" }} />
                  </div>
                )}
              </div>

              {/* Badge Count */}
              <div className="absolute top-1.5 md:top-2 right-1.5 md:right-2">
                <div className={`px-1.5 md:px-2 py-0.5 md:py-1 rounded-md md:rounded-lg text-[10px] md:text-xs font-bold ${
                  activeTab === "ayat" ? "bg-white/20 text-white" : "bg-primary/10 text-primary"
                }`}>
                  {surah?.numberOfAyahs || 0}
                </div>
              </div>
            </button>

            {/* Tab: Membaca */}
            <button
              ref={membacaRef}
              onClick={() => setActiveTab("membaca")}
              className={`relative overflow-hidden rounded-xl md:rounded-2xl p-3 md:p-4 text-left transition-all duration-300 flex-shrink-0 min-w-[180px] snap-center ${
                activeTab === "membaca"
                  ? "bg-gradient-to-br from-primary to-primary/80 text-white shadow-xl shadow-primary/25 scale-[1.02]"
                  : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {activeTab === "membaca" && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
              )}

              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center ${
                    activeTab === "membaca" ? "bg-white/20" : "bg-primary/10"
                  }`}>
                    <BookOpen className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base md:text-lg">
                      {isEnglish ? "Read" : "Membaca"}
                    </h3>
                    <p className={`text-xs md:text-sm ${activeTab === "membaca" ? "text-white/80" : "text-muted-foreground"}`}>
                      {isEnglish ? "Reading mode" : "Mode baca"}
                    </p>
                  </div>
                </div>

                {activeTab === "membaca" && (
                  <div className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3 md:w-4 md:h-4 animate-pulse" />
                  </div>
                )}
              </div>

              {/* Coming Soon Badge */}
              <div className="absolute top-1.5 md:top-2 right-1.5 md:right-2">
                <div className={`px-1.5 md:px-2 py-0.5 md:py-1 rounded-md md:rounded-lg text-[10px] md:text-xs font-bold ${
                  activeTab === "membaca" ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
                }`}>
                  {isEnglish ? "Soon" : "Segera"}
                </div>
              </div>
            </button>
            {/* Tab: Per Kata */}
            <button
              ref={perkataRef}
              onClick={() => setActiveTab("perkata")}
              className={`relative overflow-hidden rounded-xl md:rounded-2xl p-3 md:p-4 text-left transition-all duration-300 flex-shrink-0 min-w-[180px] snap-center ${
                activeTab === "perkata"
                  ? "bg-gradient-to-br from-primary to-primary/80 text-white shadow-xl shadow-primary/25 scale-[1.02]"
                  : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {activeTab === "perkata" && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
              )}

              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center ${
                    activeTab === "perkata" ? "bg-white/20" : "bg-primary/10"
                  }`}>
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base md:text-lg">
                      {isEnglish ? "Word-by-word" : "Per Kata"}
                    </h3>
                    <p className={`text-xs md:text-sm ${activeTab === "perkata" ? "text-white/80" : "text-muted-foreground"}`}>
                      {isEnglish ? "Inline meaning" : "Arti per kata"}
                    </p>
                  </div>
                </div>

                {activeTab === "perkata" && (
                  <div className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3 md:w-4 md:h-4 animate-pulse" />
                  </div>
                )}
              </div>
            </button>
            </div>
          </div>

          {/* Tab: Ayat - Mode Studi */}
          <TabsContent value="ayat" className="mt-4">
            {(loading || qcfPending) ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-4 space-y-3">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {surah?.ayahs?.map((ayah, index) => {
                  const bookmark = getAyahBookmark(ayah.numberInSurah);
                  const highlightClass = bookmark?.color ? highlightColors.find(c => c.value === bookmark.color)?.class : "";
                  const qcfAyah = arabicFont === "qcf" ? qcfData?.[ayah.numberInSurah] : null;

                  return (
                    <Card
                      key={ayah.number}
                      className={`${currentAyah === ayah.numberInSurah ? "border-primary" : ""} ${highlightClass} transition-colors`}
                      data-testid={`ayah-${ayah.numberInSurah}`}
                      id={`ayah-${ayah.numberInSurah}`}
                    >
                      <CardContent className="p-3 md:p-4">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-2 md:mb-3">
                          <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary/10 text-primary text-xs md:text-sm font-semibold flex items-center justify-center">
                            {ayah.numberInSurah}
                          </div>
                          <div className="flex items-center gap-1.5 md:gap-3">
                            <AyahRecorder
 verseKey={`${surahNumber}:${ayah.numberInSurah}`}
 memorized={memorizedAyahs.includes(ayah.numberInSurah)}
 onPassed={() => handleVoicePassed(ayah.numberInSurah)}
 />
<Button
                              variant="ghost"
                              size="icon"
                              className={`rounded-full h-7 w-7 md:h-8 md:w-8 text-primary ${memorizedAyahs.includes(ayah.numberInSurah) ? "" : "hidden"}`}
                              onClick={() => handleToggleMemorized(ayah.numberInSurah, false)}
                            >
                              <span
                                className={`inline-flex items-center justify-center w-3.5 h-3.5 md:w-4 md:h-4 rounded-full border ${
                                  memorizedAyahs.includes(ayah.numberInSurah)
                                    ? "bg-primary border-primary text-primary-foreground"
                                    : "border-muted-foreground"
                                }`}
                              >
                                <Check className="w-2 md:w-2.5 h-2 md:h-2.5" />
                              </span>
                            </Button>
                            <div className="flex gap-1 md:gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="rounded-full h-7 w-7 md:h-8 md:w-8"
                              onClick={() => handlePlayAyah(index)}
                              disabled={probingReciter}
                              data-testid={`play-ayah-${ayah.numberInSurah}`}
                            >
                              {probingReciter ? (
                                <Loader2 className="w-3 h-3 md:w-3.5 md:h-3.5 animate-spin" />
                              ) : (
                                <Play className="w-3 h-3 md:w-3.5 md:h-3.5" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="rounded-full h-7 w-7 md:h-8 md:w-8"
                              onClick={() => handleOpenTafsir(ayah.numberInSurah)}
                              data-testid={`tafsir-ayah-${ayah.numberInSurah}`}
                              title={isEnglish ? "View Tafsir" : "Lihat Tafsir"}
                            >
                              <BookOpen className="w-3 h-3 md:w-3.5 md:h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`rounded-full h-7 w-7 md:h-8 md:w-8 ${bookmark ? "text-primary" : ""}`}
                              onClick={() => toggleBookmark(ayah.numberInSurah)}
                              data-testid={`bookmark-ayah-${ayah.numberInSurah}`}
                            >
                              {bookmark ? (
                                <Bookmark className="w-3 h-3 md:w-3.5 md:h-3.5 fill-current" />
                              ) : (
                                <BookmarkPlus className="w-3 h-3 md:w-3.5 md:h-3.5" />
                              )}
                            </Button>
                            </div>
                          </div>
                        </div>

                        {/* Arabic */}
                        {(() => { if (ayah.numberInSurah <= 3) console.log(`QCF_DEBUG: render ayat ${ayah.numberInSurah}, qcfAyah=`, qcfAyah, "qcfFontsReady=", qcfFontsReady); return null; })()}
                        {qcfAyah ? (
                          <p
                            className={`${arabicSizeClass} text-right leading-[2.4] md:leading-[2.8] mb-3 md:mb-4`}
                            style={{ fontFamily: `qcf-page-${qcfAyah.page_number}, 'Amiri', serif` }}
                            dir="rtl"
                          >
                            {qcfAyah.text}
                          </p>
                        ) : (
                          <p className={`${arabicFontClass} ${arabicSizeClass} text-right leading-[2.4] md:leading-[2.8] mb-3 md:mb-4`} dir="rtl">
                            {stripBismillah(ayah.arabic, ayah.numberInSurah)}
                          </p>
                        )}

                        <Separator className="my-3 md:my-4" />

                        {/* Translation */}
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                          {translation === "indonesian" ? ayah.indonesian : ayah.english}
                        </p>

                        {/* Bookmark Note */}
                        {bookmark?.note && (
                          <div className="mt-2 md:mt-3 p-2 bg-muted/50 rounded-lg">
                            <p className="text-xs md:text-sm text-muted-foreground italic">{bookmark.note}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Tab: Per Kata */}
          <TabsContent value="perkata" className="mt-4">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-4 space-y-3">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {surah?.ayahs?.map((ayah) => (
                  <PerKataAyahRow
                    key={ayah.number}
                    ayah={ayah}
                    isEnglish={isEnglish}
                    wwEntry={wwCache[String(ayah.numberInSurah)]}
                    wwIsLoading={!!wwLoading[String(ayah.numberInSurah)]}
                    onVisible={loadWordByWord}
                    stripBismillah={stripBismillah}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="membaca" className="mt-4">
            <ReadingMode key={surahNumber} surahNumber={parseInt(surahNumber)} currentAyah={audioIsPlaying ? audioCurrentAyah : null} isEnglish={isEnglish} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEnglish ? "Confirm memorization" : "Konfirmasi hafalan"}</DialogTitle>
          </DialogHeader>
          <div>
            {isEnglish ? `Are you sure you memorized verse ${confirmAyah}?` : `Yakin sudah menghafal ayat ${confirmAyah}?`}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            <Button onClick={confirmMemorized} disabled={updatingMemorization}>
              {isEnglish ? "Yes, memorized" : "Ya, sudah hafal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bookmark/Highlight Dialog */}
      <Dialog open={showBookmarkDialog} onOpenChange={setShowBookmarkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {getAyahBookmark(selectedAyah)
                ? (isEnglish ? "Edit Bookmark" : "Ubah Bookmark")
                : (isEnglish ? "Add Bookmark" : "Tambah Bookmark")
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Color Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {isEnglish ? "Highlight Color" : "Warna Highlight"}
              </label>
              <div className="flex gap-2">
                {highlightColors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setBookmarkData({ ...bookmarkData, color: bookmarkData.color === color.value ? "" : color.value })}
                    className={`w-10 h-10 rounded-lg border-2 ${color.class} ${
                      bookmarkData.color === color.value ? "ring-2 ring-offset-2 ring-primary" : ""
                    } transition-all`}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {isEnglish ? "Notes (optional)" : "Catatan (opsional)"}
              </label>
              <textarea
                value={bookmarkData.note}
                onChange={(e) => setBookmarkData({ ...bookmarkData, note: e.target.value })}
                placeholder={isEnglish ? "Add your notes..." : "Tambahkan catatan..."}
                className="w-full min-h-[100px] p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowBookmarkDialog(false);
                setBookmarkData({ color: "", note: "" });
                setSelectedAyah(null);
              }}
              disabled={savingBookmark}
            >
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            <Button onClick={handleSaveBookmark} disabled={savingBookmark}>
              {savingBookmark ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEnglish ? "Saving..." : "Menyimpan..."}
                </>
              ) : (
                getAyahBookmark(selectedAyah)
                  ? (isEnglish ? "Update" : "Perbarui")
                  : (isEnglish ? "Save" : "Simpan")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tafsir Panel Dialog */}
      <Dialog open={showTafsirPanel} onOpenChange={setShowTafsirPanel}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEnglish ? "Tafsir Interaktif" : "Tafsir Interaktif"}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowTafsirPanel(false)}
                className="rounded-full"
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          {tafsirAyah && surah && (
            <TafsirPanel
              surahNumber={parseInt(surahNumber)}
              ayahNumber={tafsirAyah}
              surahName={surah.name}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuranReader;
